import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChartColumn, ClipboardCheck, House, NotebookPen, UserRound, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import LogoMark from 'components/global/LogoMark';
import { PortalAccountMenu } from './PortalAccountMenu';
import { PortalBalancePill } from './PortalBalancePill';
import { PortalTestsMenu } from './PortalTestsMenu';
import { portalNavLinkClass } from './navLinkClass';
import { TelegramStartParamHandler } from 'components/TelegramStartParamHandler';
import { TelegramBackButton } from 'components/TelegramBackButton';
import { useAuthContext } from 'modules/auth/context';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { IS_IO } from 'lib/site';
import { upsellClass } from 'components/portal/upsell';

/** examing.io has no marketplace: no home feed and no tests catalog. */
const IO_HIDDEN = new Set(['/portal', '/portal/tests', '/portal/mistakes', '/portal/words']);

/** Desktop top-bar destinations; "Testlar" renders as the category menu. */
const NAV_ITEMS: { to: string; end?: boolean; labelKey: string }[] = [
  { to: '/portal', end: true, labelKey: 'portal.home' },
  { to: '/portal/tests', labelKey: 'portal.tests' },
  { to: '/portal/mistakes', labelKey: 'portal.mistakes' },
  { to: '/portal/words', labelKey: 'portal.words' },
  { to: '/portal/settings/history', labelKey: 'portal.history' },
].filter((item) => !IS_IO || !IO_HIDDEN.has(item.to));

const isHistory = (path: string) => path.startsWith('/portal/settings/history');

/** Phone tab bar; the icons are lucide's equivalents of the board's drawn shapes. History lives under settings, so "Profil" is current on every other settings page and on So'zlar (reached from the profile). */
const TABS: { key: string; to: string; labelKey: string; icon: LucideIcon; isActive: (path: string) => boolean }[] = [
  { key: 'home', to: '/portal', labelKey: 'portal.home', icon: House, isActive: (p) => p === '/portal' || p === '/portal/' },
  { key: 'tests', to: '/portal/tests', labelKey: 'portal.tests', icon: ClipboardCheck, isActive: (p) => p.startsWith('/portal/tests') },
  { key: 'mistakes', to: '/portal/mistakes', labelKey: 'portal.mistakes', icon: NotebookPen, isActive: (p) => p.startsWith('/portal/mistakes') },
  { key: 'history', to: '/portal/settings/history', labelKey: 'portal.history', icon: ChartColumn, isActive: isHistory },
  {
    key: 'account',
    to: '/portal/settings',
    labelKey: 'portal.account',
    icon: UserRound,
    isActive: (p) => (p.startsWith('/portal/settings') && !isHistory(p)) || p.startsWith('/portal/words'),
  },
].filter((tab) => !IS_IO || !IO_HIDDEN.has(tab.to));

const TAB_COLUMNS: Record<number, string> = { 2: 'grid-cols-2', 4: 'grid-cols-4', 5: 'grid-cols-5' };

const pillFocus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Who lands here is decided by the router's role-based experience switch (see isCandidateExperience). */
export const CandidatePortalLayout: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { state } = useAuthContext();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { data: subscription } = usePortalSubscription();
  const hasActiveSubscription = Boolean(
    subscription && new Date(subscription.periodEnd).getTime() > Date.now(),
  );
  const activePlanName = hasActiveSubscription
    ? subscription?.planName
    : state.organization?.tier &&
      state.organization.tier !== 'FREE' &&
      state.organization.tier !== 'EXPIRED'
      ? state.organization.tier
      : null;

  return (
    <div data-surface="portal" className="min-h-dvh bg-muted/30 text-foreground">
      {/* Telegram deep link (startapp=<code>) — renders nothing outside Telegram */}
      <TelegramStartParamHandler />
      {/* Native Telegram back chevron, wired to the router. Browser: no-op. */}
      <TelegramBackButton />
      <header className="sticky top-0 z-30 border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 lg:h-16 lg:gap-6 lg:px-7">
          <Link
            to="/portal"
            className={cn('-my-2 -ml-2 flex min-h-11 min-w-11 shrink-0 items-center gap-[9px] rounded-md pl-2 text-foreground md:gap-2.5 lg:my-0 lg:ml-0 lg:min-h-0 lg:min-w-0 lg:pl-0', pillFocus)}
          >
            {/* The mark's alt text names the link; the wordmark would only repeat it. */}
            <LogoMark size={28} className="rounded-[8px]" />
            <span aria-hidden="true" className="hidden font-display text-base font-extrabold tracking-[-0.02em] min-[380px]:inline md:text-[17px]">
              Examing
            </span>
          </Link>
          <nav aria-label={t('portal.navigation')} className="hidden items-center gap-1 lg:flex">
            {NAV_ITEMS.map(({ to, end, labelKey }) =>
              to === '/portal/tests' ? (
                <PortalTestsMenu key={to} />
              ) : (
                <NavLink key={to} to={to} end={end} className={({ isActive }) => portalNavLinkClass(isActive)}>
                  {t(labelKey)}
                </NavLink>
              ),
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2 md:gap-2.5">
            {IS_IO ? null : activePlanName ? (
              // A status, not an upsell: the live plan is a lime chip with a hairline ring (the board's "Pro").
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className={cn(
                  'relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[""] lg:before:hidden inline-flex h-7 shrink-0 items-center gap-2 rounded-[10px] border border-primary-600/40 bg-primary px-3 text-xs font-bold text-primary-foreground transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-sm active:translate-y-0 md:h-8 md:text-[12.5px]',
                  pillFocus,
                  'focus-visible:ring-offset-1 focus-visible:ring-offset-card',
                )}
              >
                <span className="md:hidden">{activePlanName}</span>
                <span className="hidden md:inline">{t('portal_nav_plan_pill', { plan: activePlanName })}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className={cn(
                  'relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[""] lg:before:hidden inline-flex h-7 shrink-0 items-center rounded-full px-3.5 text-xs font-bold md:h-8 md:text-[12.5px]',
                  upsellClass,
                )}
              >
                {t('upgrade')}
              </button>
            )}
            {!IS_IO && <PortalBalancePill />}
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl overflow-x-clip px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-5 max-lg:[@media(max-height:480px)]:pb-[calc(4.25rem+env(safe-area-inset-bottom))] md:px-7 md:pt-9 lg:pb-14">
        <Outlet />
      </main>

      {/* Phone and tablet tab bar (< 1024px, landscape phones included): the current tab's icon sits on a lime pill. */}
      <nav
        aria-label={t('portal.navigation')}
        className={cn(
          'fixed inset-x-0 bottom-0 z-30 grid h-[calc(68px+env(safe-area-inset-bottom))] border-t border-border bg-card pb-[env(safe-area-inset-bottom)] lg:hidden',
          // A phone on its side is ~375px tall: a slimmer bar leaves the page room.
          '[@media(max-height:480px)]:h-[calc(52px+env(safe-area-inset-bottom))]',
          TAB_COLUMNS[TABS.length] ?? 'grid-cols-2',
        )}
      >
        {TABS.map(({ key, to, labelKey, icon: Icon, isActive }) => {
          const active = isActive(pathname);
          return (
            <Link
              key={key}
              to={to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex min-w-0 flex-col items-center justify-center gap-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                active ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-[26px] w-11 items-center justify-center rounded-full transition-colors',
                  active && 'bg-primary text-primary-foreground',
                )}
              >
                <Icon size={20} strokeWidth={1.9} />
              </span>
              <span className="line-clamp-2 max-w-full break-words px-0.5 text-center leading-[1.15]">{t(labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      {!IS_IO && <PlanCheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} subscription={subscription} />}
    </div>
  );
};

export default CandidatePortalLayout;
